import { RadioButtonCard, RadioGroup } from '@orn/shared-web/components/RadioButton/RadioButtons';
import * as React from 'react';

import { OnboardingTitle } from '../../OnboardingLayout';
import { truncateForCount } from '../dependants/helpers';
import { createEmptyPendingDependant, useOnboardingContext, type DependantCount } from '../OnboardingContext';

import OnboardingStepActions from './OnboardingStepActions';

import type { IOnboardingStepProps } from '../types';

/**
 * Loved-ones count picker. The first screen of the dependant flow — captures
 * "one person" vs. "two people with shared finances" by sizing
 * `pendingDependants` to the chosen count (the array length *is*
 * `dependantCount`). The chosen value persists across back-navigation within
 * the onboarding flow's React tree.
 *
 * On resume (or back-nav) the radio pre-selects to the existing count: the
 * flow seeds `pendingDependants` from the user's saved dependants before this
 * screen mounts, so `dependantCount` is already populated and the user can
 * continue forward without re-picking.
 *
 * On continue, resizes `pendingDependants` to the chosen count — growing pads
 * empty slots, shrinking captures any dropped server row into
 * `pendingDependantDeletions` so the review step deletes it rather than
 * orphaning it.
 *
 * Figma: Mantle - UX/UI, node 4154:68017 ("Onboarding Step 07 - Loved Ones Amount").
 */
const DependantCountStep = ({ onNext, onBack }: IOnboardingStepProps) => {
  const { dependantCount, pendingDependants, setPendingDependants, setPendingDependantDeletions } =
    useOnboardingContext();
  const [selected, setSelected] = React.useState<DependantCount | null>(dependantCount);

  const handleContinue = () => {
    if (selected === null) {
      return;
    }
    if (pendingDependants.length !== selected) {
      const next = truncateForCount(pendingDependants, selected, createEmptyPendingDependant);
      // Capture any dropped slot's serverId so the review step can delete it.
      // Slots without a serverId are pure local state and just disappear.
      const droppedServerIds = pendingDependants
        .slice(selected)
        .map((slot) => slot.serverId)
        .filter((id): id is string => id !== null);
      if (droppedServerIds.length > 0) {
        // Dedupe — a count yo-yo (e.g. 2→1→2→1) would otherwise queue the
        // same id twice, firing redundant (but harmless) delete calls.
        setPendingDependantDeletions((prev) => Array.from(new Set([...prev, ...droppedServerIds])));
      }
      setPendingDependants(next);
    }
    onNext();
  };

  return (
    <>
      <OnboardingTitle title="Who are you helping?" onBack={onBack} />

      <div className="flex w-full flex-col gap-6">
        <RadioGroup
          aria-label="Number of loved ones"
          value={selected ? String(selected) : null}
          onChange={(next) => setSelected(next === '2' ? 2 : 1)}
          className="gap-2"
        >
          <RadioButtonCard value="1" label="One person" />
          <RadioButtonCard
            value="2"
            label="Two people with shared finances"
            hint="e.g. A couple with shared bank accounts, joint expenses, or co-mingled assets"
          />
        </RadioGroup>

        <OnboardingStepActions onContinue={handleContinue} isContinueDisabled={selected === null} />
      </div>
    </>
  );
};

DependantCountStep.displayName = 'DependantCountStep';

export default DependantCountStep;
